import Icon from 'icons/Icon'
import debounce from 'lodash/debounce'
import React, { useCallback, useEffect, useState } from 'react'

/**
 * GameSearch Component
 *
 * Provides a search input for filtering games. The input value updates immediately
 * for quick visual feedback, while the filtering logic is debounced to avoid
 * triggering expensive state updates on every keystroke.
 *
 * @param {Object} props - Component properties.
 * @param {Object} props.languageData - Contains text for the placeholder (multilingual support).
 * @param {Function} props.setSelectedFilter - Function to update selected filter state.
 * @param {Object} props.selectedFilter - Current selected filter state object.
 *
 * @returns {JSX.Element} Search input UI for game filtering.
 */
const GameSearch = ({
  languageData,
  setSelectedFilter,
  selectedFilter
}) => {
  const [searchTerm, setSearchTerm] = useState(selectedFilter?.search || '')

  // Keep local search term in sync with selectedFilter.search (e.g. when cleared or initialized)
  useEffect(() => {
    setSearchTerm(selectedFilter?.search || '')
  }, [selectedFilter?.search])

  // Debounce only the heavy filter update part
  const handleDebouncedSearch = useCallback(
    debounce((value) => {
      setSelectedFilter(prev => {
        // Guard to avoid redundant updates if search term matches
        if ((prev?.search || '') === value) return prev

        return {
          ...prev,
          search: value,
          subCategoryId: null,
          category: null,
          provider: [],
          studio: [],
          rating: []
        }
      })
    }, 500),
    [setSelectedFilter]
  )

  // Clean up debounce on unmount
  useEffect(() => {
    return () => {
      handleDebouncedSearch.cancel()
    }
  }, [handleDebouncedSearch])

  /**
   * Handles real-time input change for UI responsiveness and
   * applies debounced filtering updates.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - Input change event
   */
  const handleChange = (e) => {
    const value = e.target.value
    setSearchTerm(value)
    handleDebouncedSearch(value)
  }

  const handleClear = () => {
    setSearchTerm('')
    handleDebouncedSearch.cancel()
    setSelectedFilter(prev => ({ ...prev, search: '' }))
  }

  return (
    <div className='search-input-wrapper'>
      <span className='search-icon'>
        <Icon
          name={searchTerm ? 'xmark-large' : 'search'}
          width='16px'
          onClick={() => (searchTerm ? handleClear() : null)}
          className={searchTerm ? 'search-icon-btn-clear' : 'search-icon-btn-search'}
        />
      </span>

      <input
        className='search-input w-100'
        placeholder={languageData?.quickSearch}
        value={searchTerm}
        onChange={handleChange}
      />
    </div>
  )
}

export default GameSearch
